import { useEffect, useMemo, useRef } from "react";
import { ackMailNotifications, fetchPendingMailNotifications } from "@/lib/api/notifications";
import {
  INBOX_BOOTSTRAP_FOLDER_GAP_MS,
  MAIL_LIVE_SYNC_MS,
  MULTI_ACCOUNT_INBOX_BACKGROUND_SYNC_INITIAL_MS,
} from "@/lib/constants/inbox";
import { isDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import { isOutboxSendInFlight } from "@/lib/outbox/outboxSendLock";
import {
  idleDelay,
  syncFolderFromServerCache,
  syncFolderHeaders,
} from "@/lib/mail/folderWarmup";
import { isImapClientBusy } from "@/lib/mail/imapClientLock";
import { hasMutationHold } from "@/lib/mail/mailMutationHold";
import { hasBlockingMailMutation } from "@/lib/mail/mailMutationProgress";
import type { MailAccountSummary } from "@/lib/types/mail";

const VISIBILITY_COOLDOWN_MS = 20_000;
const IMAP_WAIT_MAX_MS = 8_000;
const IMAP_WAIT_STEP_MS = 400;

type Options = {
  enabled?: boolean;
  /** Compte déjà couvert par useMailLiveSync — évite double IMAP. */
  skipAccountId?: number;
};

async function waitForImapSlot(
  cancelled: () => boolean,
  accountId: number
): Promise<boolean> {
  let waited = 0;
  while (
    !cancelled() &&
    (isDraftSaveInFlight() ||
      isOutboxSendInFlight() ||
      isImapClientBusy() ||
      hasBlockingMailMutation() ||
      hasMutationHold(accountId, "INBOX"))
  ) {
    if (waited >= IMAP_WAIT_MAX_MS) return false;
    await idleDelay(IMAP_WAIT_STEP_MS);
    waited += IMAP_WAIT_STEP_MS;
  }
  return !cancelled();
}

/**
 * Boucle multi-comptes : notifs cron + DD02 + IMAP incrémental INBOX (hors compte actif).
 */
export function useMultAccountInboxBackgroundSync(
  accounts: MailAccountSummary[],
  options?: Options
): void {
  const enabled = options?.enabled !== false;
  const skipAccountId = options?.skipAccountId;
  const runningRef = useRef(false);
  const lastVisiblePollRef = useRef(0);
  const skipRef = useRef(skipAccountId);
  skipRef.current = skipAccountId;

  const accountIds = useMemo(
    () => accounts.map((a) => a.id).sort((a, b) => a - b).join(","),
    [accounts]
  );

  useEffect(() => {
    if (!enabled || accounts.length === 0) return;

    let cancelled = false;
    const isCancelled = () => cancelled;

    const syncAccountInbox = async (accountId: number) => {
      if (document.visibilityState !== "visible") return;

      const notifRes = await fetchPendingMailNotifications(accountId);
      if (cancelled || document.visibilityState !== "visible") return;

      const events = notifRes.ok ? (notifRes.data.events ?? []) : [];
      if (events.length > 0) {
        // Son uniquement via notifyNewMailArrived (après sync réelle).
        const eventFolders = [...new Set(events.map((ev) => ev.folder).filter(Boolean))];
        for (const eventFolder of eventFolders) {
          if (cancelled || document.visibilityState !== "visible") break;
          await syncFolderFromServerCache(accountId, eventFolder);
          if (document.visibilityState !== "visible") break;
          if (await waitForImapSlot(isCancelled, accountId)) {
            await syncFolderHeaders(accountId, eventFolder);
          }
        }
        const ids = events.map((ev) => ev.id).filter((id) => id > 0);
        if (ids.length > 0) {
          await ackMailNotifications(ids);
        }
      }

      if (cancelled || document.visibilityState !== "visible") return;

      await syncFolderFromServerCache(accountId, "INBOX");
      if (cancelled || document.visibilityState !== "visible") return;

      if (await waitForImapSlot(isCancelled, accountId)) {
        await syncFolderHeaders(accountId, "INBOX");
      }
    };

    const pollAllInboxes = async () => {
      if (
        cancelled ||
        document.visibilityState !== "visible" ||
        runningRef.current ||
        isOutboxSendInFlight()
      ) {
        return;
      }

      const targets = accounts.filter((account) => account.id !== skipRef.current);
      if (targets.length === 0) return;

      runningRef.current = true;
      try {
        for (const account of targets) {
          if (cancelled) break;
          await syncAccountInbox(account.id);
          if (cancelled) break;
          await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
        }
      } finally {
        runningRef.current = false;
      }
    };

    const initialTimer = window.setTimeout(() => {
      void pollAllInboxes();
    }, MULTI_ACCOUNT_INBOX_BACKGROUND_SYNC_INITIAL_MS);

    const intervalId = window.setInterval(() => {
      void pollAllInboxes();
    }, MAIL_LIVE_SYNC_MS);

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisiblePollRef.current < VISIBILITY_COOLDOWN_MS) return;
      lastVisiblePollRef.current = now;
      void pollAllInboxes();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accountIds, accounts, enabled]);
}
